/**
 * Idle-time scene warm-up, one scene at a time.
 *
 * A scene's first frame is its most expensive one: geometry uploads, the last of its shader
 * work, texture uploads. If it happens while the visitor is scrolling toward the scene, the
 * page freezes for a moment right there. After the intro the visitor is usually reading the
 * hero, so `SceneCanvas` queues its scene here: each one mounts offscreen, draws that first
 * frame, and only then does the next one start, so the costs never stack up.
 *
 * @module
 */
import { whenIdle } from './prefetch'

type Job = () => Promise<void>

const queue: Job[] = []
let draining = false

async function drain(): Promise<void> {
  if (draining) return
  draining = true
  while (queue.length > 0) {
    const job = queue.shift()!
    await new Promise<void>((resolve) => whenIdle(resolve, 2500))
    await job()
  }
  draining = false
}

/**
 * Queues `run` (which mounts a scene and resolves once it has drawn) behind the scenes
 * already waiting. Returns a cancel function; a cancelled job is skipped.
 */
export function queueSceneWarmup(run: () => Promise<void>): () => void {
  let cancelled = false
  queue.push(async () => {
    if (!cancelled) await run()
  })
  void drain()
  return () => {
    cancelled = true
  }
}
